import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  InputAdornment,
  Divider,
  Chip,
  Collapse,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { calcPriceInclVat, calcPriceExclVat, formatPrice, roundMoney } from '../../utils/vat';
import DecimalField from '../shared/DecimalField';
import HelpButton from '../shared/HelpButton';
import { useUserStore } from '../../store';

import { can } from '../../utils/access';
import VatModeToggle from '../shared/VatModeToggle';
import VatConverseLine from '../shared/VatConverseLine';
function EditPartDialog({ open, handleClose, part, maintenanceId, setMaintenance, maintenance, onSwapEstimate }) {
  const { t } = useTranslation();
  const { currentOrganization } = useUserStore();
  const orgDefaultVat = currentOrganization?.default_price_includes_vat ?? false;
  const canEditPrices = can(currentOrganization, 'work_orders.edit_prices');
  const canSeeCosts = can(currentOrganization, 'costs.view_purchase_prices');
  const canViewMargins = canSeeCosts && can(currentOrganization, 'costs.view_margins');
  const [quantity, setQuantity] = useState(1);
  const [unitPrice, setUnitPrice] = useState('');
  const [useAutoPrice, setUseAutoPrice] = useState(true);
  const [loading, setLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [priceIncludesVat, setPriceIncludesVat] = useState(false);

  const [description, setDescription] = useState('');
  const [discountPercent, setDiscountPercent] = useState('');
  const [invoiceTarget, setInvoiceTarget] = useState('insurance');

  // Part details editing state
  const [isEditingPart, setIsEditingPart] = useState(false);
  const [partName, setPartName] = useState('');
  const [partManufacturerCode, setPartManufacturerCode] = useState('');
  const [partManufacturerName, setPartManufacturerName] = useState('');
  const [savingPart, setSavingPart] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);

  useEffect(() => {
    if (part) {
      setQuantity(parseFloat(part.quantity) || 1);
      setDescription(part.description || '');
      setDiscountPercent(parseFloat(part.discount_percent) > 0 ? parseFloat(part.discount_percent) : '');
      setSelectedJobId(part.maintenance_job_id || null);
      // price_manually_set indicates if auto-calculation is disabled
      const isManuallySet = part.price_manually_set === true;
      setUseAutoPrice(!isManuallySet);
      setDeleteConfirm(false);
      // Reset part editing state
      setIsEditingPart(false);
      setPartName(part.name || '');
      setPartManufacturerCode(part.manufacturer_code || '');
      setPartManufacturerName(part.manufacturer_name || '');
      setInvoiceTarget(part.invoice_target || 'insurance');
      // Set price and toggle based on how it was originally entered
      if (part.is_margin_vat_scheme) {
        // Margin scheme: always load gross customer price
        setPriceIncludesVat(true);
        setUnitPrice(part.unit_price_incl_vat || part.effective_price_incl_vat || '');
      } else if (part.price_entered_with_vat && part.unit_price_incl_vat) {
        // User previously saved with VAT-included — restore
        setPriceIncludesVat(true);
        setUnitPrice(part.unit_price_incl_vat);
      } else if (!part.price_manually_set) {
        // Price was auto-calculated (never manually overridden) — use org default
        setPriceIncludesVat(orgDefaultVat);
        if (orgDefaultVat) {
          const storedInclVat = part.unit_price_incl_vat || part.effective_price_incl_vat;
          if (storedInclVat && (part.price_entered_with_vat || part.part_price_entered_with_vat)) {
            setUnitPrice(storedInclVat);
          } else {
            const price = parseFloat(part.effective_price || part.unit_price || 0);
            const vr = parseFloat(part.vat_rate || 25.5);
            setUnitPrice(calcPriceInclVat(price, vr));
          }
        } else {
          setUnitPrice(part.effective_price || part.unit_price || '');
        }
      } else {
        // User manually set price as VAT-excluded — respect that
        setPriceIncludesVat(false);
        setUnitPrice(part.effective_price || part.unit_price || '');
      }
    }
  }, [part, orgDefaultVat]);

  const handleSave = async () => {
    setLoading(true);
    try {
      // Get VAT rate first (needed for calculations)
      const vatRate = parseFloat(part?.vat_rate || 25.5);

      // Get the entered price value
      const enteredPriceValue = parseFloat(unitPrice) || 0;

      // Calculate VAT-exclusive price for storage
      let priceExclVat = enteredPriceValue;
      let priceInclVat = null;
      const enteredWithVat = !useAutoPrice && priceIncludesVat && enteredPriceValue !== 0;

      if (enteredWithVat) {
        // User entered VAT-inclusive price - store it as source of truth
        priceInclVat = enteredPriceValue;
        priceExclVat = calcPriceExclVat(enteredPriceValue, vatRate);
      }

      let data;
      if (part.is_margin_vat_scheme) {
        // Margin scheme: always send gross customer price
        data = {
          quantity: parseFloat(quantity) || 1,
          unit_price_incl_vat: useAutoPrice ? null : parseFloat(unitPrice),
          price_entered_with_vat: true,
          unit_price: useAutoPrice ? null : parseFloat(unitPrice),
          description: description,
          discount_percent: parseFloat(discountPercent) || 0,
          invoice_target: invoiceTarget,
          maintenance_job_id: selectedJobId,
        };
      } else {
        data = {
          quantity: parseFloat(quantity) || 1,
          unit_price: useAutoPrice ? null : priceExclVat,
          ...(useAutoPrice ? {} : {
            unit_price_incl_vat: enteredWithVat ? priceInclVat : null,
            price_entered_with_vat: enteredWithVat,
          }),
          description: description,
          discount_percent: parseFloat(discountPercent) || 0,
          invoice_target: invoiceTarget,
          maintenance_job_id: selectedJobId,
        };
      }
      const response = await axios.patch(
        `/api/maintenances/${maintenanceId}/update_part/${part.id}/`,
        data
      );
      setMaintenance(response.data);
      handleClose();
    } catch (err) {
      console.error('Error updating part:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) {
      setDeleteConfirm(true);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.delete(
        `/api/maintenances/${maintenanceId}/remove_part/${part.id}/`
      );
      setMaintenance(response.data);
      handleClose();
    } catch (err) {
      console.error('Error removing part:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setDeleteConfirm(false);
    handleClose();
  };

  const handleClearDeclined = async () => {
    setLoading(true);
    try {
      const response = await axios.patch(
        `/api/maintenances/${maintenanceId}/update_part/${part.id}/`,
        { confirmation_status: 'none' }
      );
      setMaintenance(response.data);
      handleClose();
    } catch (err) {
      console.error('Error clearing declined status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSavePartDetails = async () => {
    if (!partName.trim()) return;
    setSavingPart(true);
    try {
      // Update the Part model directly
      await axios.patch(`/api/parts/${part.part_id}/`, {
        name: partName.trim(),
        manufacturer_code: partManufacturerCode.trim(),
        manufacturer_name: partManufacturerName.trim(),
      });
      // Refresh the maintenance to get updated part data
      const response = await axios.get(`/api/maintenances/${maintenanceId}/`);
      setMaintenance(response.data);
      setIsEditingPart(false);
    } catch (err) {
      console.error('Error updating part details:', err);
    } finally {
      setSavingPart(false);
    }
  };

  const handleCancelEditPart = () => {
    setIsEditingPart(false);
    setPartName(part.name || '');
    setPartManufacturerCode(part.manufacturer_code || '');
    setPartManufacturerName(part.manufacturer_name || '');
  };

  const enteredPrice = parseFloat(unitPrice || 0);
  const vatRate = parseFloat(part?.vat_rate || 25.5);
  const discountMultiplier = 1 - (parseFloat(discountPercent) || 0) / 100;
  // Calculate display values based on whether price includes VAT
  const effectivePriceExclVat = priceIncludesVat ? calcPriceExclVat(enteredPrice, vatRate) : enteredPrice;
  const effectivePriceInclVat = priceIncludesVat ? enteredPrice : calcPriceInclVat(enteredPrice, vatRate);
  const lineTotalExclVat = roundMoney(quantity * effectivePriceExclVat * discountMultiplier);
  const lineTotalInclVat = (parseFloat(discountPercent) || 0) > 0
    ? roundMoney(lineTotalExclVat * (1 + vatRate / 100))
    : roundMoney(quantity * effectivePriceInclVat);
  const vatAmount = roundMoney(lineTotalInclVat - lineTotalExclVat);
  const costPrice = parseFloat(part?.cost_price || 0);
  const costPriceInclVat = part?.part_stored_price_incl_vat
    ? parseFloat(part.part_stored_price_incl_vat)
    : (costPrice > 0 ? calcPriceInclVat(costPrice, vatRate) : 0);
  // Margin is measured against the price actually charged, not the list price: a
  // discount comes straight out of the workshop's own margin, so reporting the
  // pre-discount figure here tells the seller he is earning roughly twice what he is.
  const chargedUnitPriceExclVat = roundMoney(effectivePriceExclVat * discountMultiplier);
  const markupPercent = costPrice > 0 && chargedUnitPriceExclVat > 0
    ? ((chargedUnitPriceExclVat - costPrice) / costPrice) * 100
    : null;
  const grossMarginPercent = costPrice > 0 && chargedUnitPriceExclVat > 0
    ? ((chargedUnitPriceExclVat - costPrice) / chargedUnitPriceExclVat) * 100
    : null;
  const marginAmount = costPrice > 0 && chargedUnitPriceExclVat > 0
    ? chargedUnitPriceExclVat - costPrice
    : null;
  // What the whole row earns, which is what actually matters on a 4-off line.
  const lineMarginAmount = marginAmount !== null ? roundMoney(marginAmount * quantity) : null;
  const lineDiscountAmount = (parseFloat(discountPercent) || 0) > 0
    ? roundMoney(quantity * effectivePriceExclVat - lineTotalExclVat)
    : null;

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: '16px' },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Typography
          sx={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontWeight: 600,
            fontSize: '18px',
          }}
        >
          {t('Edit part')}
        </Typography>
        <IconButton onClick={handleCancel} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* Part Info */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: '8px' }}>
          {!isEditingPart ? (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: '16px' }}>
                    {partName}
                  </Typography>
                  {!part.is_pientarvikelisa && !part.is_estimate && (
                    <IconButton
                      size="small"
                      onClick={() => setIsEditingPart(true)}
                      sx={{ p: 0.5, color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                    >
                      <EditIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {part.is_margin_vat_scheme ? (
                    <Chip
                      label={t('Margin VAT')}
                      size="small"
                      color="info"
                      sx={{ fontSize: '11px', height: '22px' }}
                    />
                  ) : (
                    <Chip
                      label={`${t('VAT')} ${formatPrice(vatRate, 1)} %`}
                      size="small"
                      sx={{ fontSize: '11px', height: '22px' }}
                    />
                  )}
                </Box>
              </Box>
              {(partManufacturerCode || partManufacturerName) && (
                <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                  {partManufacturerName && `${partManufacturerName} • `}
                  {partManufacturerCode}
                </Typography>
              )}
              {part.is_estimate && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1 }} data-testid="edit-part-estimate-notice">
                  <Typography sx={{ fontSize: '13px', color: 'text.secondary', flex: 1, minWidth: 160 }}>
                    {t('quoteMachine.swap.editNotice')}
                  </Typography>
                  {onSwapEstimate && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      onClick={() => onSwapEstimate(part)}
                      data-testid="edit-part-swap-estimate"
                      sx={{ textTransform: 'none', borderRadius: '8px' }}
                    >
                      {t('quoteMachine.swap.button')}
                    </Button>
                  )}
                </Box>
              )}
            </>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
                  {t('Edit part details')}
                </Typography>
                <Chip
                  label={`${t('VAT')} ${formatPrice(vatRate, 1)} %`}
                  size="small"
                  sx={{ fontSize: '11px', height: '22px' }}
                />
              </Box>
              <TextField
                label={t('Part Name')}
                value={partName}
                onChange={(e) => setPartName(e.target.value)}
                size="small"
                required
                fullWidth
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              />
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <TextField
                  label={t('Manufacturer')}
                  value={partManufacturerName}
                  onChange={(e) => setPartManufacturerName(e.target.value)}
                  size="small"
                  fullWidth
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                />
                <TextField
                  label={t('Part Code')}
                  value={partManufacturerCode}
                  onChange={(e) => setPartManufacturerCode(e.target.value)}
                  size="small"
                  fullWidth
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  onClick={handleCancelEditPart}
                  disabled={savingPart}
                  sx={{ borderRadius: '6px', textTransform: 'none' }}
                >
                  {t('Cancel')}
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleSavePartDetails}
                  disabled={savingPart || !partName.trim()}
                  sx={{ borderRadius: '6px', textTransform: 'none' }}
                >
                  {savingPart ? t('Saving...') : t('Save')}
                </Button>
              </Box>
            </Box>
          )}
        </Box>

        {/* Customer Declined Banner */}
        {part.confirmation_status === 'denied' && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              bgcolor: 'error.lighter',
              borderRadius: '8px',
              border: '1px solid',
              borderColor: 'error.light',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CancelIcon sx={{ color: 'error.main', fontSize: 20 }} />
              <Typography sx={{ fontSize: '14px', color: 'error.dark', fontWeight: 500 }}>
                {t('Customer declined')}
              </Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              onClick={handleClearDeclined}
              disabled={loading}
              sx={{
                borderColor: 'error.main',
                color: 'error.main',
                borderRadius: '6px',
                textTransform: 'none',
                fontSize: '12px',
                '&:hover': {
                  borderColor: 'error.dark',
                  bgcolor: 'error.lighter',
                },
              }}
            >
              {t('Clear status')}
            </Button>
          </Box>
        )}

        {/* Description */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
            {t('Description')}
          </Typography>
          <TextField
            multiline
            minRows={2}
            maxRows={6}
            value={description}
            onChange={(e) => setDescription(e.target.value.slice(0, 500))}
            placeholder={t('Describe part details...')}
            fullWidth
            size="small"
            inputProps={{ maxLength: 500 }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
          />
          <Typography sx={{ fontSize: '12px', color: 'text.secondary', mt: 0.5 }}>
            {t('Optional. Shows on invoice if filled.')} ({description.length}/500)
          </Typography>
        </Box>

        {/* Related job link */}
        {maintenance?.jobs?.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
              {t('Related to job')}
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={selectedJobId || ''}
                onChange={(e) => setSelectedJobId(e.target.value || null)}
                displayEmpty
                sx={{ borderRadius: '8px', fontSize: '14px' }}
              >
                <MenuItem value="">
                  <em>{t('No link')}</em>
                </MenuItem>
                {maintenance.jobs.map((job) => (
                  <MenuItem key={job.id} value={job.id}>
                    {job.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {/* Invoice target toggle. In ALV mode it controls insurance coverage
            (covered line VAT goes to the leasing company, so "Korvattava/Ei korvattava"). */}
        {maintenance?.billing_customer && (
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
              {t('Invoice target')}
            </Typography>
            <ToggleButtonGroup
              value={invoiceTarget}
              exclusive
              onChange={(e, val) => { if (val) setInvoiceTarget(val); }}
              size="small"
              fullWidth
            >
              <ToggleButton value="insurance" sx={{ textTransform: 'none', fontSize: '13px' }}>
                {maintenance?.insurance_vat_deductible
                  ? t('Covered')
                  : (maintenance?.billing_customer_is_insurance ? t('Insurance') : t('Billing customer'))}
              </ToggleButton>
              <ToggleButton value="customer" sx={{ textTransform: 'none', fontSize: '13px' }}>
                {maintenance?.insurance_vat_deductible ? t('Not covered') : t('Customer')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        {/* Quantity & Discount */}
        <Box sx={{ mb: 3, display: 'flex', gap: 3 }}>
          <Box>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
              {t('Quantity')}
            </Typography>
            <DecimalField
              value={quantity}
              onChange={setQuantity}
              placeholder="1"
              size="small"
              sx={{
                width: '120px',
                '& .MuiOutlinedInput-root': { borderRadius: '8px' },
              }}
            />
          </Box>
          {!maintenance?.billing_customer_is_insurance && (
            <Box>
              <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
                {t('Discount %')}
              </Typography>
              <DecimalField
                value={discountPercent}
                disabled={!canEditPrices}
                onChange={(val) => {
                  const num = parseFloat(val);
                  if (!isNaN(num) && num > 100) return;
                  setDiscountPercent(val);
                }}
                size="small"
                placeholder="0"
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                sx={{
                  width: '100px',
                  '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                }}
              />
            </Box>
          )}
        </Box>

        {/* Margin Info */}
        {costPrice > 0 && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'info.lighter', borderRadius: '8px', border: '1px solid', borderColor: 'info.light' }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 500, mb: 1, color: 'info.dark' }}>
              {t('Margin info')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 3 }}>
              <Box>
                <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                  {t('Cost price')}
                </Typography>
                <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                  {formatPrice(costPrice)} €
                </Typography>
              </Box>
              {canSeeCosts && (
                <Box>
                  <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                    {t('Purchase price')} ({t('incl. VAT')} {formatPrice(vatRate)} %)
                  </Typography>
                  <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                    {formatPrice(costPriceInclVat)} €
                  </Typography>
                </Box>
              )}
              {canViewMargins && grossMarginPercent !== null && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                      {t('Margin')}
                    </Typography>
                    <HelpButton
                      size={14}
                      sx={{ p: 0 }}
                      title={t('Margin vs. markup')}
                      text={t('margin_vs_markup_help')}
                    />
                  </Box>
                  <Typography
                    sx={{ fontSize: '14px', fontWeight: 500 }}
                    color={marginAmount < 0 ? 'error.main' : 'text.primary'}
                  >
                    {formatPrice(grossMarginPercent, 1)} % ({formatPrice(marginAmount)} €)
                  </Typography>
                  <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                    {t('Markup')}: {formatPrice(markupPercent, 1)} %
                  </Typography>
                  {quantity > 1 && lineMarginAmount !== null && (
                    <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                      {t('Line margin')}: {formatPrice(lineMarginAmount)} €
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
            {canViewMargins && lineDiscountAmount !== null && (
              <Typography sx={{ fontSize: '11px', color: 'text.secondary', mt: 1 }}>
                {t('Margin is shown after the line discount')} (−{formatPrice(lineDiscountAmount)} €)
              </Typography>
            )}
            {part.price_manually_set && (
              <Typography sx={{ fontSize: '11px', color: 'warning.dark', mt: 1 }}>
                {t('Price manually set - automatic margin not applied')}
              </Typography>
            )}
          </Box>
        )}

        {/* Price */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
            {part.is_margin_vat_scheme ? t('Customer price') : (priceIncludesVat ? t('Selling price (incl. VAT)') : t('Selling price (excl. VAT)'))}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DecimalField
                value={unitPrice}
                disabled={!canEditPrices}
                onChange={(value) => {
                  setUnitPrice(value);
                  setUseAutoPrice(false);
                }}
                allowNegative={!part.is_margin_vat_scheme}
                size="small"
                InputProps={{
                  endAdornment: <InputAdornment position="end">€</InputAdornment>,
                }}
                sx={{
                  width: '150px',
                  '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                }}
              />
              {!part.is_margin_vat_scheme && (
                <VatModeToggle
                  value={priceIncludesVat ? 'incl' : 'excl'}
                  onChange={(e, val) => {
                    if (val) {
                      setPriceIncludesVat(val === 'incl');
                      setUseAutoPrice(false);  // Toggle change is a manual price decision
                    }
                  }}
                  sx={{ flexShrink: 0 }}
                />
              )}
              {!useAutoPrice && !part.is_estimate && (
                <Button
                  size="small"
                  onClick={() => {
                    // Reset to auto mode - show auto-calculated price immediately
                    const autoPrice = part.auto_calculated_price;
                    if (part.is_margin_vat_scheme) {
                      setPriceIncludesVat(true);
                      setUnitPrice(autoPrice || '');
                    } else {
                      setPriceIncludesVat(orgDefaultVat);
                      if (orgDefaultVat && autoPrice) {
                        const vr = parseFloat(part.vat_rate || 25.5);
                        setUnitPrice(calcPriceInclVat(parseFloat(autoPrice), vr));
                      } else {
                        setUnitPrice(autoPrice || '');
                      }
                    }
                    setUseAutoPrice(true);
                  }}
                  sx={{ textTransform: 'none' }}
                >
                  {t('Reset to auto')}
                </Button>
              )}
            </Box>
            {/* Show calculated price (not for margin scheme) */}
            {!part.is_margin_vat_scheme && (
              <VatConverseLine value={unitPrice} mode={priceIncludesVat ? 'incl' : 'excl'} vatRate={vatRate} />
            )}
            {parseFloat(unitPrice) < 0 && (
              <Typography sx={{ fontSize: '12px', color: 'warning.main' }}>
                {t('negativePriceHint')}
              </Typography>
            )}
            {costPrice > 0 && (
              <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                {useAutoPrice
                  ? (part?.part_sell_price ? t('Price from registry sell price') : t('Price calculated with margin'))
                  : t('Custom price - margin not applied')}
              </Typography>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Line Total with VAT breakdown */}
        {part.is_margin_vat_scheme ? (
          /* Simplified view for margin VAT parts */
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                {t('Selling price (incl. VAT)')}
              </Typography>
              <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>
                {formatPrice(lineTotalInclVat)} €
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '12px', color: 'text.secondary', fontStyle: 'italic' }}>
              {t('VAT is calculated on profit margin (margin VAT scheme)')}
            </Typography>
          </Box>
        ) : (
          /* Normal VAT breakdown for regular parts */
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                {t('Subtotal (excl. VAT)')}
              </Typography>
              <Typography sx={{ fontSize: '14px' }}>
                {formatPrice(lineTotalExclVat)} €
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                {t('VAT')} ({formatPrice(vatRate, 1)} %)
              </Typography>
              <Typography sx={{ fontSize: '14px' }}>
                {formatPrice(vatAmount)} €
              </Typography>
            </Box>
            <Divider sx={{ my: 0.5 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                {t('Total (incl. VAT)')}
              </Typography>
              <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>
                {formatPrice(lineTotalInclVat)} €
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'space-between' }}>
        <Button
          color="error"
          startIcon={<DeleteOutlineIcon />}
          onClick={handleDelete}
          disabled={loading}
          sx={{
            borderRadius: '8px',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          {deleteConfirm ? <span>{t('Confirm delete')}</span> : <span>{t('Delete')}</span>}
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            onClick={handleCancel}
            sx={{
              borderRadius: '8px',
              px: 2.5,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading}
            sx={{
              bgcolor: 'secondary.main',
              borderRadius: '8px',
              px: 2.5,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              '&:hover': { bgcolor: 'secondary.dark' },
            }}
          >
            {t('Save')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default EditPartDialog;
